import { Component, type ErrorInfo, type ReactNode } from "react";
import { reportError } from "../lib/errorCapture";

type Props = {
  children: ReactNode;
  /**
   * Optional custom fallback. Receives `reset()` to clear the error and
   * re-render the children. When omitted, a generic recoverable panel
   * is shown.
   */
  fallback?: (reset: () => void) => ReactNode;
  /**
   * When this value changes, a *caught* error is cleared so the subtree
   * re-renders — without remounting it. AppLayout passes the route
   * pathname so navigating away from a crashed page recovers on its own.
   *
   * Use this instead of `key={pathname}`. A `key` change unmounts and
   * remounts the entire subtree on *every* navigation — including routed
   * sub-dialogs that belong to the current page (e.g. the intrusion-log
   * dialog at `/heatmap/:mac/:start/:stop`). That tears down and rebuilds
   * expensive children like map surfaces on each dialog open/close.
   * `resetKey` does work only when there is actually an error to clear;
   * with no error a key change is a no-op and the subtree is preserved.
   */
  resetKey?: unknown;
};

type State = { error: Error | null; lastResetKey: unknown };

/**
 * Catches render- and commit-phase errors in the subtree and shows a
 * recoverable fallback, instead of letting React tear the whole root
 * down — the white-screen failure mode.
 *
 * Concrete motivating bug: GitHub issue #184 — clicking a drone
 * white-screened Chrome with an uncaught `insertBefore NotFoundError`.
 * Root cause was browser auto-translation mutating the DOM under React;
 * the translation opt-out in index.html fixes that specific trigger,
 * and this boundary is the defence-in-depth so the *next* unknown
 * trigger degrades one page instead of the entire app.
 *
 * This is the W1 ("error containment") down-payment from
 * Documentation/domains/map-view/resilience-deep-dive.md. Follow-ups
 * tracked there: route `componentDidCatch` into a telemetry sink (W0),
 * and add finer-grained per-map-surface boundaries (W1).
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null, lastResetKey: this.props.resetKey };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  static getDerivedStateFromProps(
    props: Props,
    state: State,
  ): Partial<State> | null {
    if (Object.is(props.resetKey, state.lastResetKey)) return null;
    // resetKey changed (a navigation). Drop any caught error so the new
    // route renders cleanly. With no error this is a no-op — the subtree
    // is reconciled normally and never remounted.
    return { error: null, lastResetKey: props.resetKey };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // Route into the one structured sink (resilience-deep-dive W0) so
    // boundary-caught errors land alongside escaped window errors.
    reportError(error, {
      source: "react-error-boundary",
      componentStack: info.componentStack ?? undefined,
    });
  }

  reset = (): void => this.setState({ error: null });

  render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;

    if (this.props.fallback) return this.props.fallback(this.reset);

    return (
      <div className="flex min-h-[60vh] w-full items-center justify-center p-6">
        <div className="w-full max-w-sm rounded-lg bg-white p-6 text-center shadow-lg">
          <h2 className="mb-1 text-lg font-bold text-black">
            Something went wrong on this page
          </h2>
          <p className="mb-4 text-sm text-zinc-500">
            The page hit an unexpected error. The rest of the app is still
            available — try again, or reload.
          </p>
          <div className="flex justify-center gap-2">
            <button
              type="button"
              onClick={this.reset}
              className="rounded bg-secondary px-4 py-2 text-sm font-medium text-white transition-colors hover:opacity-90"
            >
              Try again
            </button>
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="rounded border border-zinc-300 px-4 py-2 text-sm font-medium text-zinc-700 transition-colors hover:bg-zinc-50"
            >
              Reload
            </button>
          </div>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
